import { useCallback, useEffect, useRef } from 'react'

import type { GradeKind } from '@/lib/api/tea-grade-api'
import { getAnalyticsActor, posthog } from '@/lib/posthog'

/** Kết quả người dùng thấy sau một lượt tải tồn của dòng bung. */
export type TeaGradeStockResult = 'in_stock' | 'empty' | 'error'

type TeaGradeStockEvent =
    { stage: 'opened' } | { stage: 'loaded'; result: TeaGradeStockResult }

/**
 * Chỉ gửi enum + actor: không id phân loại, không số kg/số lô (dữ liệu kho),
 * đúng contract ở `docs/analytics/tea-grade-stock.md`. Lỗi analytics không
 * được chặn thao tác bung dòng.
 */
function capture(event: TeaGradeStockEvent, catalog: GradeKind) {
    const identity = getAnalyticsActor()
    if (!identity) return
    try {
        posthog.capture('tea_grade_stock_viewed', {
            // v2 (M-39): thêm `catalog` — mỗi kho có danh mục riêng.
            schema_version: 2,
            catalog,
            ...event,
            ...identity,
        })
    } catch {
        // Analytics failure must not block expanding the row.
    }
}

interface StockState {
    /** Phân loại đang bung; null = không bung dòng nào. */
    expandedId: string | null
    isLoading: boolean
    isError: boolean
    /** Số dòng kho trả về (0 = không còn lô tồn). */
    rowCount: number
    /** Danh mục của kho nào (M-39). Mặc định kho phân loại. */
    catalog?: GradeKind
}

/**
 * Đo màn Loại phân loại: bao nhiêu lần người dùng bung xem tồn (`opened`, phát
 * từ chính cú bấm) và mỗi lượt tải kết thúc ra sao (`loaded`, một lần cho mỗi
 * lượt tải: bung dòng, hoặc bấm Thử lại sau lỗi).
 */
export function useTeaGradeStockTracking({
    expandedId,
    isLoading,
    isError,
    rowCount,
    catalog = 'sorting',
}: StockState) {
    // Lượt đã báo kết quả. Về null mỗi khi đang tải, để lần settle kế tiếp
    // (Thử lại) được báo; đổi dòng bung là lượt mới dù dữ liệu có sẵn trong cache.
    const reportedFor = useRef<string | null>(null)

    useEffect(() => {
        if (!expandedId) {
            reportedFor.current = null
            return
        }
        if (isLoading) {
            reportedFor.current = null
            return
        }
        if (reportedFor.current === expandedId) return
        reportedFor.current = expandedId
        capture(
            {
                stage: 'loaded',
                result: isError ? 'error' : rowCount > 0 ? 'in_stock' : 'empty',
            },
            catalog,
        )
    }, [expandedId, isLoading, isError, rowCount, catalog])

    const trackOpened = useCallback(
        () => capture({ stage: 'opened' }, catalog),
        [catalog],
    )

    return { trackOpened }
}
